import React, { useEffect, useState } from "react";
import { api, ApiClientError } from "../../api";
import { humanizeKey, formatSmart } from "../../lib/dashboardFormat";

export interface DrilldownTarget {
  title: string;
  endpoint: string;
  params?: Record<string, any>;
  rows?: any[];
}

// Test #30 ("every KPI can drill down to source records"): the backend
// attaches either the raw rows inline or a `{endpoint, params}` pointer to
// a real MVP-A/B/C/D list endpoint. This modal handles both — it never
// invents its own aggregate, it only ever shows the real underlying rows.
export default function DrilldownModal({ target, onClose }: { target: DrilldownTarget; onClose: () => void }) {
  const [rows, setRows] = useState<any[] | null>(target.rows || null);
  const [loading, setLoading] = useState(!target.rows);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (target.rows) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .drilldown(target.endpoint, target.params)
      .then((data) => {
        if (cancelled) return;
        // The drilldown target is usually a flat list endpoint (array
        // response), but a few contract-defined drilldowns point at
        // another dashboard endpoint instead (e.g. the executive summary's
        // own pointer to `/dashboard/funnel`) — fall back to the first
        // array field found on the object so there's still something
        // tabular to show rather than a blank modal.
        let list: any[];
        if (Array.isArray(data)) list = data;
        else if (data && typeof data === "object") {
          const firstArrayField = Object.values(data).find((v) => Array.isArray(v) && v.length > 0) as any[] | undefined;
          list = firstArrayField || (data ? [data] : []);
        } else list = [];
        setRows(list);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e instanceof ApiClientError ? e.message : "No se pudo cargar el detalle.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target.endpoint, JSON.stringify(target.params)]);

  const columns = React.useMemo(() => {
    if (!rows || rows.length === 0) return [];
    const keys = new Set<string>();
    rows.slice(0, 20).forEach((r) => {
      if (r && typeof r === "object") Object.keys(r).forEach((k) => keys.add(k));
    });
    return Array.from(keys).filter((k) => {
      const sample = rows.find((r) => r && r[k] !== undefined)?.[k];
      return typeof sample !== "object" || sample === null;
    }).slice(0, 8);
  }, [rows]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-wide" onClick={(e: any) => e.stopPropagation()}>
        <h2 style={{ marginTop: 0 }}>{target.title}</h2>
        <p className="meta">
          Registros fuente ({target.endpoint}
          {target.params && Object.keys(target.params).length > 0 ? ` · ${Object.entries(target.params).map(([k, v]) => `${k}=${v}`).join(", ")}` : ""}).
        </p>
        {loading && <div className="loading-strip">Cargando registros...</div>}
        {error && <p className="form-error">{error}</p>}
        {!loading && !error && rows && rows.length === 0 && <p className="meta">Sin registros para este recorte.</p>}
        {!loading && !error && rows && rows.length > 0 && (
          <div style={{ maxHeight: 420, overflow: "auto" }}>
            <table>
              <thead>
                <tr>
                  {columns.map((c) => (
                    <th key={c}>{humanizeKey(c)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 100).map((r, i) => (
                  <tr key={r?.id ?? i}>
                    {columns.map((c) => (
                      <td key={c}>{formatSmart(c, r?.[c])}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {rows.length > 100 && <p className="meta">Mostrando los primeros 100 de {rows.length} registros.</p>}
          </div>
        )}
        <div className="modal-actions">
          <button className="btn-ghost" onClick={onClose}>
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
